import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import {
  DEFAULT_PRODUCT_COLOR,
  PRODUCT_SOURCE_LABELS,
  PRODUCT_STATUS_LABELS,
  ProductSwatch,
  formatPrice,
  mapProductItem,
  mapProposal,
} from './productConfigUtils';
import './ProductsPage.css';

export default function ProductsPage() {
  const { selectedCompanyId, permissions } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [sync, setSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [expandedProposalId, setExpandedProposalId] = useState(null);

  const canView = Boolean(permissions?.canViewProducts || permissions?.canViewBots);
  const canEdit = Boolean(permissions?.canManageProducts || permissions?.canManageBots);

  const loadProducts = useCallback(async () => {
    if (!selectedCompanyId || !canView) {
      setProducts([]);
      setProposals([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await DashboardApi.getCompanyProducts(selectedCompanyId, { includeArchived: true });
      setProducts(Array.isArray(response?.items) ? response.items.map(mapProductItem).filter((item) => item.id) : []);
      setProposals(Array.isArray(response?.proposals) ? response.proposals.map(mapProposal).filter((item) => item.id) : []);
      setSync(response?.sync || null);
      setError(null);
    } catch (err) {
      const message = err.message || 'Kunne ikke hente produkter.';
      setError(message);
      notifyError(message, { source: 'Produkter' });
    } finally {
      setLoading(false);
    }
  }, [canView, selectedCompanyId]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const activeProducts = useMemo(
    () => products.filter((product) => product.status !== 'archived'),
    [products],
  );
  const archivedProducts = useMemo(
    () => products.filter((product) => product.status === 'archived'),
    [products],
  );
  const pendingProposals = useMemo(
    () => proposals.filter((proposal) => proposal.status === 'pending'),
    [proposals],
  );

  const openCreatePage = () => {
    navigate('/konfigurasjon/produkter/ny');
  };

  const openEditPage = (product) => {
    if (product?.id) navigate(`/konfigurasjon/produkter/${product.id}`);
  };

  const toggleArchive = async (product) => {
    if (!selectedCompanyId || !canEdit || !product?.id) return;
    setSaving(true);
    try {
      if (product.status === 'archived') {
        await DashboardApi.restoreCompanyProduct(selectedCompanyId, product.id);
      } else {
        await DashboardApi.archiveCompanyProduct(selectedCompanyId, product.id);
      }
      await loadProducts();
      notifySuccess(product.status === 'archived' ? 'Produktet er aktivert.' : 'Produktet er arkivert.', { source: 'Produkter' });
    } catch (err) {
      notifyError(err.message || 'Kunne ikke endre produktstatus.', { source: 'Produkter' });
    } finally {
      setSaving(false);
    }
  };

  const handleProposalAction = async (proposalId, action) => {
    if (!selectedCompanyId || !canEdit || !proposalId) return;
    setSaving(true);
    try {
      if (action === 'approve') {
        await DashboardApi.approveCompanyProductProposal(selectedCompanyId, proposalId, {});
      } else {
        await DashboardApi.rejectCompanyProductProposal(selectedCompanyId, proposalId, {});
      }
      await loadProducts();
      notifySuccess(action === 'approve' ? 'Forslaget er godkjent.' : 'Forslaget er avvist.', { source: 'Produkter' });
    } catch (err) {
      notifyError(err.message || 'Kunne ikke oppdatere forslag.', { source: 'Produkter' });
    } finally {
      setSaving(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (!selectedCompanyId || !canEdit || pendingProposals.length === 0) return;
    setSaving(true);
    try {
      await DashboardApi.bulkUpdateCompanyProductProposals(selectedCompanyId, {
        action,
        proposalIds: pendingProposals.map((proposal) => proposal.id),
      });
      await loadProducts();
      notifySuccess(action === 'approve' ? 'Forslagene er godkjent.' : 'Forslagene er avvist.', { source: 'Produkter' });
    } catch (err) {
      notifyError(err.message || 'Kunne ikke oppdatere forslag.', { source: 'Produkter' });
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  const renderProductRow = (product, archived = false) => {
    const price = formatPrice(product);
    const assignedNames = product.assignedBots.length > 0
      ? product.assignedBots.map((bot) => bot.name).join(', ')
      : 'Ingen bot-tilgang';
    return (
      <div key={product.id} className={`products-row${archived ? ' is-archived' : ''}`}>
        <div className="products-row-main">
          <ProductSwatch color={product.colorCode} />
          <button type="button" className="products-row-title" onClick={() => openEditPage(product)}>
            <span>{product.name}</span>
            <small>{[PRODUCT_SOURCE_LABELS[product.sourceKind] || product.sourceKind, price, product.category].filter(Boolean).join(' · ')}</small>
          </button>
        </div>
        <div className="products-row-bots" title={assignedNames}>
          {assignedNames}
        </div>
        <div className="products-row-actions">
          <span className={`products-status products-status--${product.status}`}>
            {PRODUCT_STATUS_LABELS[product.status] || product.status}
          </span>
          <button type="button" className="button button-secondary button-small" onClick={() => openEditPage(product)}>
            Rediger
          </button>
          <button type="button" className="button button-secondary button-small" onClick={() => toggleArchive(product)} disabled={!canEdit || saving}>
            {archived ? 'Aktiver' : 'Arkiver'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="page products-page">
      <div className="page-header products-page-header">
        <div>
          <h1>Produkter/Tjenester</h1>
          <p className="page-subtitle">
            Administrer katalogen på selskapsnivå og bestem hvilke boter som kan bruke hvert produkt.
          </p>
        </div>
        <div className="products-header-actions">
          <button type="button" className="button button-secondary" onClick={loadProducts} disabled={loading}>
            Oppdater
          </button>
          <button type="button" className="button button-primary" onClick={openCreatePage} disabled={!canEdit || saving}>
            Nytt produkt
          </button>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}

      <section className="products-summary">
        <div>
          <span>{activeProducts.length}</span>
          <p>Aktive produkter</p>
        </div>
        <div>
          <span>{pendingProposals.length}</span>
          <p>Ventende forslag</p>
        </div>
        <div>
          <span>{sync?.latestRun?.bot_name || sync?.latestRun?.source_kind ? 'Aktiv' : 'Ingen'}</span>
          <p>Siste webscan</p>
        </div>
      </section>

      <section className="products-section">
        <div className="products-section-header">
          <h2>Aktive produkter</h2>
          <span>{activeProducts.length} produkter</span>
        </div>
        {loading ? (
          <div className="products-empty">Laster produkter…</div>
        ) : activeProducts.length === 0 ? (
          <div className="products-empty">Ingen aktive produkter er lagt inn ennå.</div>
        ) : (
          <div className="products-list">
            {activeProducts.map((product) => renderProductRow(product))}
          </div>
        )}
      </section>

      <section className="products-section">
        <div className="products-section-header">
          <div>
            <h2>Forslag fra webscan</h2>
            <p>Forslagene kommer fra botenes nettsideskann, men godkjennes inn i selskapets katalog.</p>
          </div>
          <div className="products-section-actions">
            <button type="button" className="button button-secondary button-small" onClick={() => handleBulkAction('reject')} disabled={!canEdit || saving || pendingProposals.length === 0}>
              Avvis alle
            </button>
            <button type="button" className="button button-primary button-small" onClick={() => handleBulkAction('approve')} disabled={!canEdit || saving || pendingProposals.length === 0}>
              Godkjenn alle
            </button>
          </div>
        </div>
        {pendingProposals.length === 0 ? (
          <div className="products-empty">Ingen ventende produktforslag.</div>
        ) : (
          <div className="products-list">
            {pendingProposals.map((proposal) => {
              const expanded = expandedProposalId === proposal.id;
              const price = proposal.proposedPriceDisplay
                || (proposal.proposedPriceAmount && proposal.proposedPriceCurrency
                  ? `${proposal.proposedPriceAmount} ${proposal.proposedPriceCurrency}`
                  : null);
              return (
                <div key={proposal.id} className="products-row products-row--proposal">
                  <div className="products-row-main">
                    <ProductSwatch color={DEFAULT_PRODUCT_COLOR} />
                    <button
                      type="button"
                      className="products-row-title"
                      onClick={() => setExpandedProposalId(expanded ? null : proposal.id)}
                    >
                      <span>{proposal.proposedName || 'Uten navn'}</span>
                      <small>{[proposal.botName, price, proposal.proposedCategory].filter(Boolean).join(' · ')}</small>
                    </button>
                  </div>
                  <div className="products-row-bots">{proposal.proposalType === 'update' ? 'Oppdatering' : 'Nytt forslag'}</div>
                  <div className="products-row-actions">
                    <button type="button" className="button button-secondary button-small" onClick={() => handleProposalAction(proposal.id, 'reject')} disabled={!canEdit || saving}>
                      Avvis
                    </button>
                    <button type="button" className="button button-primary button-small" onClick={() => handleProposalAction(proposal.id, 'approve')} disabled={!canEdit || saving}>
                      Godkjenn
                    </button>
                  </div>
                  {expanded && (
                    <div className="products-proposal-detail">
                      {proposal.proposedShortDescription || 'Ingen beskrivelse.'}
                      {proposal.proposedUrl && <span>{proposal.proposedUrl}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {archivedProducts.length > 0 && (
        <section className="products-section">
          <div className="products-section-header">
            <h2>Arkiverte produkter</h2>
            <span>{archivedProducts.length} arkiverte</span>
          </div>
          <div className="products-list">
            {archivedProducts.map((product) => renderProductRow(product, true))}
          </div>
        </section>
      )}
    </div>
  );
}
